import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import { ImageUpload } from 'apps/shared/scripts/__generated__/graphql.generated';
import { useCreatePost } from 'apps/shared/scripts/graphql';
import {
  useAuthenticatedUser,
  useFormCallback,
  usePutSignedUrl,
} from 'apps/shared/scripts/hooks';

import { CardPostForm } from '../../components';
import { FormValues } from '../../components/CardPostForm';
import { toMediaInput } from '../../utils';

interface Props {
  cardId: string;
  closeModal: () => void;
}

const CardPostFormCreateContainer = ({ cardId, closeModal }: Props) => {
  const client = useApolloClient();
  const { mutate: createPost, isMutating: isCreating } = useCreatePost();
  const { isSuccess, onSuccess, onError } = useFormCallback();
  const { isUploading, putSignedUrl } = usePutSignedUrl();
  const user = useAuthenticatedUser();

  const onSubmit = useCallback(
    async (data: FormValues) => {
      try {
        const { media } = data;
        if (media && media.file) {
          const { result: imageUpload, errors } = await createPost({
            cardId,
            input: {
              ...data,
              media: toMediaInput(media),
            },
          });
          if (!imageUpload || errors.length > 0) {
            throw new Error(errors[0].message);
          }
          const { signedUrl } = imageUpload as ImageUpload;
          await putSignedUrl(signedUrl, media.file);
        } else {
          const { result, errors } = await createPost({ cardId, input: data });
          if (!result || errors.length > 0) {
            throw new Error(errors[0].message);
          }
        }
        onSuccess({ message: 'Success! Post was added' }, () =>
          setTimeout(closeModal, 500),
        );
        await client.refetchQueries({
          include: ['PostsByCardId'],
        });
      } catch (e) {
        onError(e);
        throw e;
      }
    },
    [cardId, client, closeModal, createPost, onError, onSuccess, putSignedUrl],
  );

  return (
    <CardPostForm
      closeModal={closeModal}
      defaultValues={user ? { name: user.name } : undefined}
      onSubmit={onSubmit}
      isSubmitting={isCreating || isUploading || isSuccess}
    />
  );
};

CardPostFormCreateContainer.displayName = 'CardPostFormCreateContainer';

export default CardPostFormCreateContainer;
